/**
 * Reminder state that must outlive a single `IncompleteUploadBanner` instance (LSP-17328).
 *
 * The banner is mounted twice in `EntityDetailPage` — once per layout branch, since the mobile and desktop
 * left columns each own their copy of the media search UI — and the two are mutually exclusive. Crossing the
 * `smAndDown` breakpoint therefore DESTROYS one mount and CREATES the other. With `dismissed` and the
 * cooldown deadlines living on the instance, a rotate, a window drag, or an iPad orientation change resurrected
 * a banner the admin had just closed, or had just successfully sent from, with a live Send Reminder on it.
 *
 * Module scope is the right lifetime for both, and no longer:
 * - It survives a remount and a breakpoint cross, which is the bug above.
 * - It does NOT survive a reload, which is what keeps dismissal non-permanent. The upload is still
 *   unfinished after the admin closes the banner, so a dismissal that persisted would hide a live problem
 *   for good; it is meant to clear the banner for now, not forever.
 *
 * Both are keyed by conversation, because both belong to the session they were earned on rather than to
 * whatever the admin happens to be looking at next.
 *
 * cursor (LSP-16985 follow-up): every mutator bumps a reactive `stateVersion`, and readers that render
 * must consume it. Plain Map/Set reads are invisible to Vue, which was survivable while at most ONE banner
 * was on screen — the mutating instance repainted itself and there was nobody else to inform. The lightbox
 * mounts the banner per carousel item (`:eager="true"`, so all of them at once), so several instances for
 * the same conversation now coexist and a send from one left its siblings painting a live Send Reminder
 * over a conversation already on cooldown.
 *
 * NOTE on what is and is not reactive here: `stateVersion` makes the SET of conversations change
 * observably. It cannot make the passage of TIME observable — `isOnCooldown` compares a stored deadline to
 * `Date.now()`, and nothing bumps the version when a window simply lapses. So a computed built on
 * `isOnCooldown` would repaint when a cooldown is RECORDED and never when it EXPIRES. Anything wanting to
 * paint a live countdown needs its own ticker; do not assume this counter provides one.
 *
 * `dismissed` and `sent` are deliberately SEPARATE (ls-heroku-ci): closing the banner and having actually
 * sent a reminder are different facts, and only one of them may block a future send. Conflating them meant
 * a banner that cannot dismiss itself (the lightbox's) also kept no record that a reminder had gone out.
 */

import { ref } from "vue";

/**
 * Bumped by every mutator below. Readers that feed a render must reference it — see the banner's
 * `sending` / `onCooldown` / `dismissed` computeds — so that all mounted instances repaint together.
 */
const stateVersion = ref(0);

/** Read this inside a computed to make that computed re-evaluate whenever reminder state changes. */
export function reminderStateVersion(): number {
	return stateVersion.value;
}

/** Absolute epoch-ms deadlines for the server cooldown, per conversation. */
const cooldownExpiryByConversation = new Map<string, number>();

/** Conversations whose banner the admin has CLOSED this page load. Says nothing about whether a reminder went out. */
const dismissedConversations = new Set<string>();

/**
 * Conversations a reminder was actually delivered for this page load.
 *
 * ls-heroku-ci (CRITICAL): this used to be implicit in `dismissedConversations`, which worked only because
 * a successful send hid the banner — there was no button left to press. The lightbox's banner may not hide
 * itself, so dismissal could no longer stand in for "a reminder went out"; the two are different facts and
 * are now recorded separately.
 *
 * WHAT THIS IS NOT: a send gate. An earlier revision of this comment claimed it was "consulted before
 * every send", which was true of a draft that blocked any second send for the page load — a policy since
 * deliberately reversed (see IncompleteUploadBanner's `alreadySent`, and lsp17329 round 5). Sending again
 * once the server's window lapses is INTENDED: the upload is still unfinished, and re-nudging a customer
 * who did not act is the feature. `sendReminder` therefore gates on the in-flight latch and the cooldown
 * only; this set feeds the button's LABEL, so a send that just happened is legible rather than surprising.
 */
const sentConversations = new Set<string>();

/**
 * Conversations with a reminder request in flight.
 *
 * Module-scoped for the same reason as the two above: it was instance-local, so a remount across the
 * `smAndDown` breakpoint mid-request produced a fresh banner whose button looked idle while the original
 * request was still unresolved — a second click, and a second real message.
 */
const inFlightConversations = new Set<string>();

export function cooldownExpiryFor(conversationId: string): number {
	return cooldownExpiryByConversation.get(conversationId) ?? 0;
}

export function recordCooldown(conversationId: string, expiresAt: number): void {
	cooldownExpiryByConversation.set(conversationId, expiresAt);
	stateVersion.value += 1;
}

export function isOnCooldown(conversationId: string): boolean {
	return cooldownExpiryFor(conversationId) > Date.now();
}

export function isDismissed(conversationId: string): boolean {
	return dismissedConversations.has(conversationId);
}

export function dismiss(conversationId: string): void {
	dismissedConversations.add(conversationId);
	stateVersion.value += 1;
}

/**
 * Forget an explicit dismissal (LSP-17965).
 *
 * The docstring above promises a dismissal "returns on the next visit or reload", and only the reload half
 * was ever true: module scope survives an in-SPA navigation, so an admin who closed the banner on one
 * conversation and came back to it later found the warning still hidden for a still-unfinished upload.
 * `EntityDetailPage` calls this for the conversation it is navigating TO, which is the "next visit" the
 * promise means — and, being keyed on an actual entity change, it cannot fire on the `smAndDown` breakpoint
 * remount that LSP-17328 deliberately made dismissal survive.
 */
export function clearDismissal(conversationId: string): void {
	dismissedConversations.delete(conversationId);
	stateVersion.value += 1;
}

/** Records that a reminder was actually delivered for this conversation. */
export function markReminderSent(conversationId: string): void {
	sentConversations.add(conversationId);
	stateVersion.value += 1;
}

/** Whether a reminder has already gone out for this conversation this page load. */
export function hasReminderBeenSent(conversationId: string): boolean {
	return sentConversations.has(conversationId);
}

export function isInFlight(conversationId: string): boolean {
	return inFlightConversations.has(conversationId);
}

export function markInFlight(conversationId: string): void {
	inFlightConversations.add(conversationId);
	stateVersion.value += 1;
}

export function clearInFlight(conversationId: string): void {
	inFlightConversations.delete(conversationId);
	stateVersion.value += 1;
}

/** Test-only: module state would otherwise leak between cases in the same file. */
export function resetIncompleteUploadReminderState(): void {
	cooldownExpiryByConversation.clear();
	dismissedConversations.clear();
	sentConversations.clear();
	inFlightConversations.clear();
	stateVersion.value += 1;
}
